import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { TrendUp, TrendDown, Wallet, Receipt, Clock, ChartLineUp } from "@phosphor-icons/react";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTHS_ES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const monthLabel = (ym) => { if(!ym) return ""; const [y,m]=ym.split("-"); return `${MONTHS_ES[parseInt(m,10)-1]} ${y.slice(2)}`; };

const currentYm = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const prevYm = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const ymOffset = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const PERIOD_LABEL_MAP = {
  current:  "Mes actual",
  previous: "Mes anterior",
  last3:    "Últimos 3 meses",
  last6:    "Últimos 6 meses",
  last12:   "Últimos 12 meses",
  custom:   "Rango personalizado",
};

export default function Finance() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("accrual");
  const [showReceivables, setShowReceivables] = useState(true);

  // ── Unified period filter — controls EVERYTHING (KPIs, chart, categoría, resumen) ──
  const [preset, setPreset] = useState("current"); // default: current month
  const [months, setMonths] = useState(12);
  const [startMonth, setStartMonth] = useState(currentYm());
  const [endMonth, setEndMonth] = useState(currentYm());
  const [useRange, setUseRange] = useState(true);

  // Apply preset → recompute state
  useEffect(() => {
    if (preset === "current") {
      setUseRange(true); setStartMonth(currentYm()); setEndMonth(currentYm());
    } else if (preset === "previous") {
      setUseRange(true); setStartMonth(prevYm()); setEndMonth(prevYm());
    } else if (preset === "last3") {
      setUseRange(true); setStartMonth(ymOffset(2)); setEndMonth(currentYm());
    } else if (preset === "last6") {
      setUseRange(true); setStartMonth(ymOffset(5)); setEndMonth(currentYm());
    } else if (preset === "last12") {
      setUseRange(true); setStartMonth(ymOffset(11)); setEndMonth(currentYm());
    }
    // "custom" → keep whatever the user is typing
  }, [preset]);

  // Fetch: single call → single source of truth
  useEffect(() => {
    const params = useRange
      ? { mode, start_month: startMonth, end_month: endMonth }
      : { mode, months };
    api.get("/reports/financial", { params }).then((r)=>setData(r.data));
  }, [mode, months, useRange, startMonth, endMonth]);

  if (!data) return <div className="p-8 text-zinc-500">Cargando…</div>;

  const r = data.receivables;
  const totalAR = r.total_mxn;
  const currentPct = totalAR > 0 ? (r.current_mxn / totalAR) * 100 : 0;
  const overduePct = totalAR > 0 ? (r.overdue_mxn / totalAR) * 100 : 0;

  // Category-view label reflects the active filter
  const categoryHeaderLabel =
    preset === "custom"
      ? (data.period_start === data.period_end
          ? monthLabel(data.period_start)
          : `${monthLabel(data.period_start)} → ${monthLabel(data.period_end)}`)
      : PERIOD_LABEL_MAP[preset] || monthLabel(data.current_month);

  // Barras: si sólo hay 1 mes de datos, hay que capar el ancho manualmente
  const monthlyCount = (data.monthly || []).length;

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">FINANZAS</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Dashboard Financiero</h1>
          <p className="text-zinc-500 mt-1 text-sm">Tipo de cambio: <span className="numeric">1 USD = {data.exchange_rate} MXN</span> · Modo: <b>{mode === "accrual" ? "Acumulación" : "Efectivo"}</b></p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <span className="text-[10px] font-mono-data uppercase tracking-widest text-zinc-500">Periodo</span>
          <div className="inline-flex rounded-sm border border-zinc-200 bg-white overflow-hidden text-xs" data-testid="finance-preset">
            {[
              { v: "current",  label: "Mes actual" },
              { v: "previous", label: "Mes anterior" },
              { v: "last3",    label: "Últimos 3m" },
              { v: "last6",    label: "Últimos 6m" },
              { v: "last12",   label: "Últimos 12m" },
              { v: "custom",   label: "Rango" },
            ].map((p) => (
              <button
                key={p.v}
                onClick={() => setPreset(p.v)}
                className={`px-3 py-1.5 font-semibold transition-colors ${preset === p.v ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
                data-testid={`preset-${p.v}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {preset === "custom" && (
            <div className="flex items-center gap-1 mt-1">
              <input type="month" value={startMonth} onChange={(e) => { setStartMonth(e.target.value); setUseRange(true); }} className="border border-zinc-200 rounded-sm px-2 py-1 text-xs h-7" data-testid="preset-start"/>
              <span className="text-xs text-zinc-400">→</span>
              <input type="month" value={endMonth} onChange={(e) => { setEndMonth(e.target.value); setUseRange(true); }} className="border border-zinc-200 rounded-sm px-2 py-1 text-xs h-7" data-testid="preset-end"/>
            </div>
          )}
        </div>
      </header>

      {/* Cuentas por cobrar */}
      <section className="border border-zinc-200 bg-white" data-testid="receivables-widget">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold">Total de cuentas por cobrar</h3>
          <span className="text-xs font-mono-data text-zinc-500">{r.current_count + r.overdue_count} notas</span>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="border border-zinc-200 bg-zinc-50/50 p-3 rounded-sm">
              <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Total facturado (con saldo)</div>
              <div className="numeric text-2xl font-bold mt-1 text-zinc-700" data-testid="ar-billed">MXN{fmt(r.billed_mxn)}</div>
              {r.billed_usd > 0 && <div className="text-[10px] text-zinc-400 numeric">+ USD${fmt(r.billed_usd)}</div>}
            </div>
            <div className="border border-[#00873D]/30 bg-[#00873D]/5 p-3 rounded-sm">
              <div className="text-[10px] uppercase font-mono-data tracking-wider text-[#00873D]/80">Ya cobrado de estas notas</div>
              <div className="numeric text-2xl font-bold mt-1 text-[#00873D]" data-testid="ar-paid">MXN{fmt(r.paid_mxn)}</div>
              {r.paid_usd > 0 && <div className="text-[10px] text-[#00873D]/70 numeric">+ USD${fmt(r.paid_usd)}</div>}
            </div>
            <div className="border border-[#E0000F]/30 bg-[#E0000F]/5 p-3 rounded-sm">
              <div className="text-[10px] uppercase font-mono-data tracking-wider text-[#E0000F]/80">Saldo pendiente por cobrar</div>
              <div className="numeric text-2xl font-bold mt-1 text-[#E0000F]" data-testid="ar-outstanding">MXN{fmt(r.total_mxn)}</div>
              {r.total_usd > 0 && <div className="text-[10px] text-[#E0000F]/70 numeric">+ USD${fmt(r.total_usd)}</div>}
            </div>
          </div>

          <div className="space-y-3">
            <div className="h-3 w-full rounded-sm bg-zinc-100 overflow-hidden flex" data-testid="ar-bar">
              {currentPct > 0 && <div className="bg-[#1E88E5] h-full" style={{ width: `${currentPct}%` }} />}
              {overduePct > 0 && <div className="bg-[#FFC000] h-full" style={{ width: `${overduePct}%` }} />}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-[#1E88E5]"/>
                <span>Actual : <b className="numeric">MXN{fmt(r.current_mxn)}</b> · {r.current_count} {r.current_count === 1 ? "nota" : "notas"}</span>
              </span>
              <span className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-[#FFC000]"/>
                <span>Vencido : <b className="numeric">MXN{fmt(r.overdue_mxn)}</b> · {r.overdue_count} {r.overdue_count === 1 ? "nota" : "notas"}
                  <button onClick={()=>setShowReceivables(!showReceivables)} className="ml-2 text-[#002FA7] hover:underline text-xs">{showReceivables ? "ocultar ▴" : "ver ▾"}</button>
                </span>
              </span>
            </div>
          </div>

          {showReceivables && r.items.length > 0 && (() => {
            const curYm = new Date().toISOString().slice(0, 7);
            // Group items by invoice_date[:7] with per-group metrics
            const groups = {};
            r.items.forEach((it) => {
              const ym = (it.invoice_date || it.due_date || "").slice(0, 7) || "sin-mes";
              if (!groups[ym]) groups[ym] = {
                items: [],
                billed_mxn: 0, billed_usd: 0,
                paid_mxn: 0, paid_usd: 0,
                outstanding_mxn: 0, outstanding_usd: 0,
                current_mxn: 0, overdue_mxn: 0,
                current_count: 0, overdue_count: 0,
              };
              const g = groups[ym];
              g.items.push(it);
              const isUsd = it.currency === "USD";
              if (isUsd) {
                g.billed_usd += it.total; g.paid_usd += it.paid; g.outstanding_usd += it.outstanding;
              } else {
                g.billed_mxn += it.total; g.paid_mxn += it.paid; g.outstanding_mxn += it.outstanding;
              }
              if (it.is_overdue) { g.overdue_count += 1; if (!isUsd) g.overdue_mxn += it.outstanding; }
              else                { g.current_count += 1; if (!isUsd) g.current_mxn += it.outstanding; }
            });
            // Sort months newest → oldest; current month always on top
            const orderedYms = Object.keys(groups).sort((a, b) => {
              if (a === curYm) return -1;
              if (b === curYm) return 1;
              return b.localeCompare(a);
            });
            return (
              <div className="space-y-4 mt-4" data-testid="receivables-groups">
                {orderedYms.map((ym) => {
                  const g = groups[ym];
                  const isCur = ym === curYm;
                  const isOld = ym !== curYm && ym < curYm;
                  const label = ym === "sin-mes" ? "Sin fecha" : monthLabel(ym);
                  const badgeColor = isCur ? "#002FA7" : isOld ? "#E0000F" : "#7E57C2";
                  const badgeLabel = isCur ? "MES ACTUAL" : isOld ? "ARRASTRADO" : "FUTURO";
                  const gTotalMxn = g.outstanding_mxn;
                  const gCurPct = gTotalMxn > 0 ? (g.current_mxn / gTotalMxn) * 100 : 0;
                  const gOvdPct = gTotalMxn > 0 ? (g.overdue_mxn / gTotalMxn) * 100 : 0;
                  return (
                    <div key={ym} className={`border rounded-sm overflow-hidden ${isCur ? "border-[#002FA7]/40" : "border-zinc-200"}`} data-testid={`ar-group-${ym}`}>
                      {/* Group header */}
                      <div className={`flex items-center justify-between px-4 py-2.5 ${isCur ? "bg-[#002FA7]/5" : "bg-zinc-50"}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading font-bold text-base">{label}</span>
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: badgeColor + "20", color: badgeColor }}>{badgeLabel}</span>
                          <span className="text-xs font-mono-data text-zinc-500">{g.items.length} {g.items.length === 1 ? "nota" : "notas"}</span>
                        </div>
                      </div>

                      {/* Per-group KPIs */}
                      <div className="px-4 pt-3 pb-2 grid grid-cols-1 sm:grid-cols-3 gap-2 border-b border-zinc-100" data-testid={`ar-group-kpis-${ym}`}>
                        <div className="border border-zinc-200 bg-zinc-50/50 p-2.5 rounded-sm">
                          <div className="text-[9px] uppercase font-mono-data tracking-wider text-zinc-500">Facturado</div>
                          <div className="numeric text-lg font-bold mt-0.5 text-zinc-700" data-testid={`ar-group-billed-${ym}`}>MX${fmt(g.billed_mxn)}</div>
                          {g.billed_usd > 0 && <div className="text-[10px] text-zinc-400 numeric">+ USD${fmt(g.billed_usd)}</div>}
                        </div>
                        <div className="border border-[#00873D]/30 bg-[#00873D]/5 p-2.5 rounded-sm">
                          <div className="text-[9px] uppercase font-mono-data tracking-wider text-[#00873D]/80">Cobrado</div>
                          <div className="numeric text-lg font-bold mt-0.5 text-[#00873D]" data-testid={`ar-group-paid-${ym}`}>MX${fmt(g.paid_mxn)}</div>
                          {g.paid_usd > 0 && <div className="text-[10px] text-[#00873D]/70 numeric">+ USD${fmt(g.paid_usd)}</div>}
                        </div>
                        <div className="border border-[#E0000F]/30 bg-[#E0000F]/5 p-2.5 rounded-sm">
                          <div className="text-[9px] uppercase font-mono-data tracking-wider text-[#E0000F]/80">Pendiente</div>
                          <div className="numeric text-lg font-bold mt-0.5 text-[#E0000F]" data-testid={`ar-group-outstanding-${ym}`}>MX${fmt(g.outstanding_mxn)}</div>
                          {g.outstanding_usd > 0 && <div className="text-[10px] text-[#E0000F]/70 numeric">+ USD${fmt(g.outstanding_usd)}</div>}
                        </div>
                      </div>

                      {/* Per-group stacked bar (vigente vs vencido) — only when there is something pending */}
                      {gTotalMxn > 0.005 && (
                        <div className="px-4 pt-3 pb-2 border-b border-zinc-100 space-y-1.5">
                          <div className="h-2 w-full rounded-sm bg-zinc-100 overflow-hidden flex" data-testid={`ar-group-bar-${ym}`}>
                            {gCurPct > 0 && <div className="bg-[#1E88E5] h-full" style={{ width: `${gCurPct}%` }} />}
                            {gOvdPct > 0 && <div className="bg-[#FFC000] h-full" style={{ width: `${gOvdPct}%` }} />}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-600">
                            {g.current_count > 0 && (
                              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#1E88E5]"/>Vigente <b className="numeric">MX${fmt(g.current_mxn)}</b> · {g.current_count}</span>
                            )}
                            {g.overdue_count > 0 && (
                              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#FFC000]"/>Vencido <b className="numeric">MX${fmt(g.overdue_mxn)}</b> · {g.overdue_count}</span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Table */}
                      <table className="w-full text-sm" data-testid={`ar-table-${ym}`}>
                        <thead className="bg-white text-[10px] uppercase text-zinc-500 border-b border-zinc-100">
                          <tr>
                            <th className="text-left px-3 py-1.5">Folio</th>
                            <th className="text-left px-3 py-1.5">Cliente</th>
                            <th className="text-left px-3 py-1.5">Vence</th>
                            <th className="text-right px-3 py-1.5">Total</th>
                            <th className="text-right px-3 py-1.5">Pagado</th>
                            <th className="text-right px-3 py-1.5">Saldo</th>
                            <th className="text-left px-3 py-1.5">Estatus</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.items.map((it) => (
                            <tr key={it.id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer" onClick={()=>navigate(`/sales?open=${it.id}`)} data-testid={`ar-row-${it.id}`}>
                              <td className="px-3 py-2 font-mono-data font-semibold text-[#002FA7] hover:underline">#{String(it.folio).padStart(6,"0")}</td>
                              <td className="px-3 py-2">{it.client_name}</td>
                              <td className="px-3 py-2 text-xs">
                                <span className={it.is_overdue ? "text-[#FFC000] font-semibold" : "text-zinc-600"}>{it.due_date}</span>
                                {it.is_overdue && <span className="text-[10px] text-[#E0000F] ml-1">({it.days_overdue}d)</span>}
                              </td>
                              <td className="px-3 py-2 text-right numeric">{it.currency==="USD"?"$":"MX$"}{fmt(it.total)}</td>
                              <td className="px-3 py-2 text-right numeric text-[#00873D]">{it.currency==="USD"?"$":"MX$"}{fmt(it.paid)}</td>
                              <td className="px-3 py-2 text-right numeric font-bold text-[#E0000F]">{it.currency==="USD"?"$":"MX$"}{fmt(it.outstanding)}</td>
                              <td className="px-3 py-2">
                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: (it.is_overdue?"#FFC000":"#1E88E5")+"20", color: it.is_overdue?"#FFC000":"#1E88E5" }}>
                                  {it.is_overdue ? "Vencido" : "Vigente"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Income & Expense chart — hereda del filtro maestro */}
      <section className="border border-zinc-200 bg-white">
        <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-baseline gap-3">
            <h3 className="font-heading text-lg font-semibold">Ingresos y gastos</h3>
            <span className="text-[10px] font-mono-data uppercase tracking-widest text-zinc-400">
              {PERIOD_LABEL_MAP[preset] || "Periodo"}
            </span>
          </div>
          <div className="flex border border-zinc-200 rounded-sm overflow-hidden text-xs" data-testid="mode-toggle">
            <button onClick={()=>setMode("accrual")} className={`px-3 py-1.5 ${mode==="accrual"?"bg-zinc-900 text-white":"bg-white text-zinc-600 hover:bg-zinc-50"}`}>Acumulación</button>
            <button onClick={()=>setMode("cash")} className={`px-3 py-1.5 ${mode==="cash"?"bg-zinc-900 text-white":"bg-white text-zinc-600 hover:bg-zinc-50"}`}>Efectivo</button>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-6 mb-4 flex-wrap text-sm">
            <div>
              <div className="flex items-center gap-2 text-zinc-500 text-xs"><span className="w-2.5 h-2.5 bg-[#00873D]"/>Ingresos reales (cobrado)</div>
              <div className="numeric text-xl font-bold text-[#00873D]" data-testid="kpi-income-paid">MX${fmt(data.total_income_paid_mxn ?? data.total_income_mxn)}</div>
            </div>
            {mode === "accrual" && (data.total_income_pending_mxn ?? 0) > 0 && (
              <div>
                <div className="flex items-center gap-2 text-zinc-500 text-xs"><span className="w-2.5 h-2.5 bg-[#FFC000]"/>Por cobrar (pendiente)</div>
                <div className="numeric text-xl font-bold text-[#FFC000]" data-testid="kpi-income-pending">MX${fmt(data.total_income_pending_mxn)}</div>
              </div>
            )}
            <div>
              <div className="flex items-center gap-2 text-zinc-500 text-xs"><span className="w-2.5 h-2.5 bg-[#E0000F]"/>Total de gastos</div>
              <div className="numeric text-xl font-bold text-[#E0000F]">MX${fmt(data.total_expense_mxn)}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-500">Resultado real (cash)</div>
              <div className={`numeric text-xl font-bold ${(data.real_net_mxn ?? data.net_mxn) >= 0 ? "text-[#002FA7]" : "text-[#E0000F]"}`} data-testid="kpi-real-net">
                MX${fmt(data.real_net_mxn ?? data.net_mxn)}
              </div>
              <div className="text-[10px] text-zinc-400">cobrado − gastos</div>
            </div>
            {mode === "accrual" && (data.total_income_pending_mxn ?? 0) > 0 && (
              <div>
                <div className="text-xs text-zinc-500">Resultado contable</div>
                <div className={`numeric text-base font-semibold ${data.net_mxn >= 0 ? "text-zinc-500" : "text-[#E0000F]"}`}>MX${fmt(data.net_mxn)}</div>
                <div className="text-[10px] text-zinc-400">incluye por cobrar</div>
              </div>
            )}
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              data={data.monthly}
              barGap={4}
              barCategoryGap={monthlyCount <= 2 ? "45%" : "20%"}
              margin={{ top: 10, right: 12, left: 0, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" />
              <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v)=>v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              <Tooltip formatter={(v)=>`MX$${fmt(v)}`} labelFormatter={monthLabel} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {/* Stack the income bar into "cobrado" + "por cobrar" so the user
                  immediately sees real cash vs receivable inside the same column. */}
              <Bar dataKey="income_paid" name="Cobrado" stackId="income" fill="#00873D" maxBarSize={72} radius={[0,0,0,0]} />
              <Bar dataKey="income_pending" name="Por cobrar" stackId="income" fill="#FFC000" maxBarSize={72} radius={[4,4,0,0]} />
              <Bar dataKey="expense" name="Gastos" fill="#E0000F" maxBarSize={72} radius={[4,4,0,0]} />
              <Bar dataKey="investment" name="Inversiones" fill="#7E57C2" maxBarSize={72} radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* KPIs (desglose clicable) */}
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <button
          type="button"
          onClick={()=>navigate(`/finance/income?mode=${mode}&months=${months}`)}
          className="text-left border border-zinc-200 p-5 bg-white hover:border-[#00873D] hover:shadow-md transition-all cursor-pointer"
          data-testid="kpi-income"
          title="Clic para ver desglose de notas"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Ingresos · periodo</div>
            <TrendUp size={20} weight="duotone" color="#00873D" />
          </div>
          <div className="numeric text-3xl font-bold mt-2 text-[#00873D] underline decoration-dotted underline-offset-4 decoration-[#00873D]/40">MX${fmt(data.total_income_mxn)}</div>
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-[#00873D] mt-2">VER DESGLOSE ↗</div>
        </button>
        <button
          type="button"
          onClick={()=>navigate(`/finance/expense?months=${months}`)}
          className="text-left border border-zinc-200 p-5 bg-white hover:border-[#E0000F] hover:shadow-md transition-all cursor-pointer"
          data-testid="kpi-expense"
          title="Gastos operativos (no incluye inversiones)"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Gastos operativos</div>
            <TrendDown size={20} weight="duotone" color="#E0000F" />
          </div>
          <div className="numeric text-3xl font-bold mt-2 text-[#E0000F] underline decoration-dotted underline-offset-4 decoration-[#E0000F]/40">MX${fmt(data.total_expense_mxn)}</div>
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-[#E0000F] mt-2">VER DESGLOSE ↗</div>
        </button>
        <div
          className="border border-zinc-200 p-5 bg-white"
          data-testid="kpi-investment"
          title="Inversiones · categorías marcadas como inversión (compra de lotes, refacciones, mercancía, etc.)"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Inversiones</div>
            <ChartLineUp size={20} weight="duotone" color="#7E57C2" />
          </div>
          <div className="numeric text-3xl font-bold mt-2 text-[#7E57C2]">MX${fmt(data.total_investment_mxn || 0)}</div>
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-[#7E57C2] mt-2">
            {(data.investments_by_category || []).length} {(data.investments_by_category || []).length === 1 ? "categoría" : "categorías"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/gastos-recurrentes")}
          className="text-left border border-zinc-200 p-5 bg-white hover:border-amber-500 hover:shadow-md transition-all cursor-pointer"
          data-testid="kpi-recurring-upcoming"
          title="Recurrentes pendientes este mes (clic para ver)"
        >
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Por venir este mes</div>
            <Clock size={20} weight="duotone" color="#d97706"/>
          </div>
          <div className="numeric text-3xl font-bold mt-2 text-amber-600 underline decoration-dotted underline-offset-4 decoration-amber-500/40">
            MX${fmt(data.recurring_upcoming_mxn || 0)}
          </div>
          <div className="text-[10px] uppercase font-mono-data tracking-wider text-amber-600 mt-2">
            {data.recurring_upcoming_count || 0} recurrente{(data.recurring_upcoming_count || 0) === 1 ? "" : "s"} pendiente{(data.recurring_upcoming_count || 0) === 1 ? "" : "s"} ↗
          </div>
        </button>
        <div className="border border-zinc-200 p-5 bg-white" data-testid="kpi-net">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500">Utilidad neta</div>
            <Wallet size={20} weight="duotone" color="#002FA7" />
          </div>
          <div className={`numeric text-3xl font-bold mt-2 ${data.net_mxn >= 0 ? "text-[#002FA7]" : "text-[#E0000F]"}`}>MX${fmt(data.net_mxn)}</div>
          {data.recurring_upcoming_mxn > 0 && (
            <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500 mt-2">
              Proyectado restando recurrentes: <b className={`numeric ${(data.projected_net_mxn || 0) >= 0 ? "text-[#002FA7]" : "text-[#E0000F]"}`}>MX${fmt(data.projected_net_mxn || 0)}</b>
            </div>
          )}
        </div>
      </section>

      {/* Investments by category (nueva sección) */}
      {(data.investments_by_category || []).length > 0 && (
        <section className="border border-[#7E57C2]/30 bg-white" data-testid="investments-section">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
                <ChartLineUp size={20} weight="duotone" className="text-[#7E57C2]"/> Inversiones · <span className="text-zinc-500 text-sm font-normal">{categoryHeaderLabel}</span>
              </h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">Compras de mercancía, refacciones, lotes, materiales para reventa. Separadas del gasto operativo.</p>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Total del periodo</div>
              <div className="numeric text-2xl font-heading font-bold text-[#7E57C2]" data-testid="investments-total">MX${fmt(data.period_investment_mxn || 0)}</div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6 items-center">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data.investments_by_category} dataKey="total_mxn" nameKey="name" cx="50%" cy="50%" outerRadius={75}>
                    {data.investments_by_category.map((c) => <Cell key={c.category_id} fill={c.color}/>)}
                  </Pie>
                  <Tooltip formatter={(v) => `MX$${fmt(v)}`}/>
                </PieChart>
              </ResponsiveContainer>
              <ul className="space-y-2 text-sm">
                {data.investments_by_category.map((c) => (
                  <li key={c.category_id} className="flex items-center justify-between gap-2" data-testid={`investment-cat-${c.category_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{background: c.color}}/>
                      <span className="truncate">{c.name}</span>
                      <span className="text-[10px] text-zinc-400 numeric">·{c.count}</span>
                    </div>
                    <span className="numeric font-semibold">MX${fmt(c.total_mxn)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Expenses by category + monthly summary — también siguen al filtro maestro */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-zinc-200 bg-white">
          <div className="px-6 py-4 border-b border-zinc-200">
            <h3 className="font-heading text-lg font-semibold">Gastos por categoría · <span className="text-zinc-500 text-sm font-normal">{categoryHeaderLabel}</span></h3>
          </div>
          <div className="p-6">
            {data.expenses_by_category.length === 0 ? (
              <div className="dotted-bg py-12 text-center text-zinc-400 text-sm">Sin gastos en este periodo</div>
            ) : (
              <div className="grid grid-cols-2 gap-6 items-center">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={data.expenses_by_category} dataKey="total_mxn" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                      {data.expenses_by_category.map((c)=><Cell key={c.category_id} fill={c.color}/>)}
                    </Pie>
                    <Tooltip formatter={(v)=>`MX$${fmt(v)}`}/>
                  </PieChart>
                </ResponsiveContainer>
                <ul className="space-y-2 text-sm">
                  {data.expenses_by_category.map((c)=>(
                    <li key={c.category_id} className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{background:c.color}}/>
                        <span className="truncate">{c.name}</span>
                      </div>
                      <span className="numeric font-semibold">MX${fmt(c.total_mxn)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="border border-zinc-200 bg-white">
          <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Resumen mensual</h3>
            <Receipt size={18} className="text-zinc-400"/>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2">Mes</th>
                <th className="text-right px-4 py-2">Ingresos</th>
                <th className="text-right px-4 py-2">Gastos</th>
                <th className="text-right px-4 py-2">Neto</th>
              </tr>
            </thead>
            <tbody>
              {data.monthly
                .filter((m) => m.month <= new Date().toISOString().slice(0, 7))
                .slice()
                .reverse()
                .map((m)=>(
                <tr key={m.month} className="border-t border-zinc-100 hover:bg-zinc-50">
                  <td className="px-4 py-2 font-medium">{monthLabel(m.month)}</td>
                  <td className="px-4 py-2 text-right numeric text-[#00873D]">MX${fmt(m.income)}</td>
                  <td className="px-4 py-2 text-right numeric text-[#E0000F]">MX${fmt(m.expense)}</td>
                  <td className={`px-4 py-2 text-right numeric font-semibold ${m.net>=0?"text-[#002FA7]":"text-[#E0000F]"}`}>MX${fmt(m.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
